import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useSeason } from "../context/SeasonContext";
import { useAuth } from "../context/AuthContext";
import { Heart } from "lucide-react";
import LazyPhoto from "../components/LazyPhoto";
import StandingsSeasonSelector from "../components/StandingsSeasonSelector";

export default function Gallery() {
  const { weekNumber } = useParams();
  const navigate = useNavigate();
  const { selectedSeason } = useSeason();
  const { isAdmin, user } = useAuth();

  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [albumsLoading, setAlbumsLoading] = useState(true);
  const [imgErrors, setImgErrors] = useState({});

  // Tagging state — admin only
  const [tagMode, setTagMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState(null);
  const [weekGames, setWeekGames] = useState([]);
  const [taggingGameId, setTaggingGameId] = useState("");
  const [tagging, setTagging] = useState(false);

  const [savedPhotoIds, setSavedPhotoIds] = useState(new Set());
  // Team filter — public
  const [teams, setTeams] = useState([]);
  const [filterTeamId, setFilterTeamId] = useState("all");

  const loadAlbums = useCallback(async () => {
    if (!selectedSeason) return;
    setAlbumsLoading(true);
    const { data } = await supabase
      .from("photo_albums")
      .select("*, photos(count)")
      .eq("season_id", selectedSeason.id)
      .order("week_number", { ascending: false });
    setAlbums((data || []).filter((a) => (a.photos?.[0]?.count ?? 0) > 0));
    setAlbumsLoading(false);
  }, [selectedSeason]);

  const loadPhotos = useCallback(async (album) => {
    setPhotosLoading(true);
    setPhotos([]);
    const { data } = await supabase
      .from("photos")
      .select(
        "*, game:games(id, team1:teams!games_team1_id_fkey(id, name, archived_at), team2:teams!games_team2_id_fkey(id, name, archived_at))",
      )
      .eq("album_id", album.id)
      .order("filename", { ascending: true });
    setPhotos(data || []);
    setPhotosLoading(false);
  }, []);

  // Load games for this album's week — for tagging dropdown
  const loadWeekGames = useCallback(
    async (album) => {
      if (!selectedSeason) return;
      // Get games within a few days of the album's sync date or just load all season games
      const { data } = await supabase
        .from("games")
        .select(
          `
                id,
                game_date,
                team1:teams!games_team1_id_fkey(id, name, archived_at),
                team2:teams!games_team2_id_fkey(id, name, archived_at)
            `,
        )
        .eq("season_id", selectedSeason.id)
        .eq("status", "finished")

        .order("game_date", { ascending: false });
      setWeekGames(data || []);

      // Also collect unique teams for filter
      const teamMap = {};
      (data || []).forEach((g) => {
        if (g.team1 && !g.team1.archived_at) teamMap[g.team1.id] = g.team1;
        if (g.team2 && !g.team2.archived_at) teamMap[g.team2.id] = g.team2;
      });
      setTeams(
        Object.values(teamMap).sort((a, b) => a.name.localeCompare(b.name)),
      );
    },
    [selectedSeason],
  );

  useEffect(() => {
    loadAlbums();
  }, [loadAlbums]);

  useEffect(() => {
    if (!weekNumber || albums.length === 0) return;
    const match = albums.find(
      (a) => String(a.week_number) === String(weekNumber),
    );
    if (match) {
      setSelectedAlbum(match);
      loadPhotos(match);
      loadWeekGames(match);
    }
  }, [weekNumber, albums, loadPhotos, loadWeekGames]);

  useEffect(() => {
    if (!user || photos.length === 0) return;
    supabase
      .from("saved_photos")
      .select("photo_id")
      .eq("profile_id", user.id)
      .in(
        "photo_id",
        photos.map((p) => p.id),
      )
      .then(({ data }) =>
        setSavedPhotoIds(new Set((data || []).map((r) => r.photo_id))),
      );
  }, [user, photos]);

  const toggleSave = async (photoId, e) => {
    e.stopPropagation();
    if (!user) return;
    const isSaved = savedPhotoIds.has(photoId);
    setSavedPhotoIds((prev) => {
      const next = new Set(prev);
      isSaved ? next.delete(photoId) : next.add(photoId);
      return next;
    });
    if (isSaved) {
      await supabase
        .from("saved_photos")
        .delete()
        .eq("profile_id", user.id)
        .eq("photo_id", photoId);
    } else {
      await supabase
        .from("saved_photos")
        .insert({ profile_id: user.id, photo_id: photoId });
    }
  };

  const openAlbum = (album) => {
    setSelectedAlbum(album);
    setImgErrors({});
    setSelectedPhotoIds(new Set());
    setLastClickedIndex(null);
    setTagMode(false);
    setFilterTeamId("all");
    loadPhotos(album);
    loadWeekGames(album);
    navigate(`/gallery/week/${album.week_number}`, { replace: true });
  };

  const closeAlbum = () => {
    setSelectedAlbum(null);
    setPhotos([]);
    setLightboxIndex(null);
    setTagMode(false);
    setSelectedPhotoIds(new Set());
    navigate("/gallery", { replace: true });
  };

  // ── Photo click handler — normal vs tag mode ───────────────────────────────
  const handlePhotoClick = (index, e) => {
    if (!tagMode) {
      openLightbox(index);
      return;
    }

    const photo = photos[index];

    if (e.shiftKey && lastClickedIndex !== null) {
      // Range select
      const start = Math.min(lastClickedIndex, index);
      const end = Math.max(lastClickedIndex, index);
      setSelectedPhotoIds((prev) => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) next.add(photos[i].id);
        return next;
      });
    } else {
      // Single toggle
      setSelectedPhotoIds((prev) => {
        const next = new Set(prev);
        if (next.has(photo.id)) next.delete(photo.id);
        else next.add(photo.id);
        return next;
      });
      setLastClickedIndex(index);
    }
  };

  // ── Tag selected photos to a game ─────────────────────────────────────────
  const applyTag = async () => {
    if (!taggingGameId) {
      alert("Select a game first.");
      return;
    }
    if (selectedPhotoIds.size === 0) {
      alert("No photos selected.");
      return;
    }

    setTagging(true);
    const gameId = taggingGameId === "untag" ? null : parseInt(taggingGameId);
    const ids = [...selectedPhotoIds];

    const { error } = await supabase
      .from("photos")
      .update({ game_id: gameId })
      .in("id", ids);

    if (error) {
      alert(`Tagging failed: ${error.message}`);
    } else {
      // Refresh photos to show updated tags
      await loadPhotos(selectedAlbum);
      setSelectedPhotoIds(new Set());
      setLastClickedIndex(null);
    }
    setTagging(false);
  };

  // ── Filtered photos for public view ───────────────────────────────────────
  const filteredPhotos = photos.filter((photo) => {
    if (filterTeamId === "all") return true;
    if (!photo.game) return false;
    return (
      String(photo.game.team1?.id) === String(filterTeamId) ||
      String(photo.game.team2?.id) === String(filterTeamId)
    );
  });

  // ── Lightbox ──────────────────────────────────────────────────────────────
  const displayPhotos = tagMode ? photos : filteredPhotos;

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const lightboxNext = (e) => {
    e.stopPropagation();
    setLightboxIndex((i) => (i + 1) % displayPhotos.length);
  };
  const lightboxPrev = (e) => {
    e.stopPropagation();
    setLightboxIndex(
      (i) => (i - 1 + displayPhotos.length) % displayPhotos.length,
    );
  };

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === "ArrowRight")
        setLightboxIndex((i) => (i + 1) % displayPhotos.length);
      if (e.key === "ArrowLeft")
        setLightboxIndex(
          (i) => (i - 1 + displayPhotos.length) % displayPhotos.length,
        );
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIndex, displayPhotos.length]);

  // ── Album Grid ────────────────────────────────────────────────────────────
  if (!selectedAlbum) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <StandingsSeasonSelector />
          <div className="p-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Photos
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {selectedSeason?.name} · Game Day Photos · Pics by{" "}
              <a
                href="https://www.instagram.com/perception_artist"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-700 dark:text-orange-300 hover:underline"
              >
                @perception_artist
              </a>
            </p>
          </div>
        </div>

        {albumsLoading ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">
            Loading albums…
          </div>
        ) : albums.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">
            No photo albums yet. Check back after game day!
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
            {albums.map((album) => (
              <button
                key={album.id}
                onClick={() => openAlbum(album)}
                className="group relative bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden text-left hover:shadow-xl transition-all duration-300 h-64"
              >
                {/* 1. The Background Image */}
                <div className="absolute inset-0">
                  <AlbumCoverStrip albumId={album.id} />
                </div>

                {/* 2. The Gradient Overlay (Scrim) */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent group-hover:from-black/95 transition-colors duration-300" />

                {/* 3. The Content (Pinned to bottom) */}
                <div className="absolute inset-0 flex flex-col justify-end p-4">
                  <div className="flex items-start justify-between">
                    <div className="drop-shadow-md">
                      <p className="text-[10px] font-black uppercase tracking-widest text-orange-400 mb-1">
                        Week {album.week_number}
                      </p>
                      <h3 className="text-lg font-bold text-white leading-tight">
                        {album.label}
                      </h3>
                    </div>

                    <span className="text-[10px] bg-white/20 backdrop-blur-md text-white border border-white/20 px-2 py-1 rounded-md font-bold shrink-0 ml-2">
                      {album.photos?.[0]?.count ?? 0} PHOTOS
                    </span>
                  </div>

                  {album.synced_at && (
                    <p className="text-[10px] text-gray-300 mt-2 font-medium opacity-80">
                      Updated{" "}
                      {new Date(album.synced_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Photo Grid View ───────────────────────────────────────────────────────
  const taggedCount = photos.filter((p) => p.game_id).length;
  const untaggedCount = photos.length - taggedCount;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 flex flex-wrap items-center gap-3">
        <button
          onClick={closeAlbum}
          className="text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400
                               transition-colors flex items-center gap-1 text-sm font-medium"
        >
          ← Back
        </button>
        <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
              {selectedAlbum.label}
            </h1>
            <span
              className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500
                                        dark:text-gray-400 px-2 py-1 rounded-full shrink-0"
            >
              {photos.length} photos
            </span>
            {taggedCount > 0 && (
              <span
                className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700
                                            dark:text-green-400 px-2 py-1 rounded-full shrink-0"
              >
                {taggedCount} tagged
              </span>
            )}
            {untaggedCount > 0 && isAdmin && (
              <span
                className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700
                                            dark:text-amber-400 px-2 py-1 rounded-full shrink-0"
              >
                {untaggedCount} untagged
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <button
              onClick={() => {
                setTagMode((t) => !t);
                setSelectedPhotoIds(new Set());
                setLastClickedIndex(null);
              }}
              className={`text-xs px-3 py-1.5 rounded-lg font-semibold border-2 transition ${
                tagMode
                  ? "bg-orange-600 border-orange-600 text-white"
                  : "border-orange-500 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              }`}
            >
              {tagMode
                ? `✓ Tagging Mode (${selectedPhotoIds.size} selected)`
                : "🏷 Tag Photos"}
            </button>
          )}
          <a
            href={`https://drive.google.com/drive/folders/${selectedAlbum.folder_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1.5 border dark:border-gray-600 rounded-lg text-gray-600
                                   dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
          >
            Open in Drive ↗
          </a>
        </div>
      </div>

      {/* ── Tagging toolbar ── */}
      {tagMode && (
        <div
          className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800
                                rounded-xl p-4 flex flex-wrap items-center gap-3"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-0.5">
              Tag Mode — click a photo to select, shift-click to select a range
            </p>
            <p className="text-xs text-orange-600 dark:text-orange-400">
              {selectedPhotoIds.size === 0
                ? "No photos selected yet"
                : `${selectedPhotoIds.size} photo${selectedPhotoIds.size !== 1 ? "s" : ""} selected`}
            </p>
          </div>
          <select
            value={taggingGameId}
            onChange={(e) => setTaggingGameId(e.target.value)}
            className="border dark:border-gray-600 rounded-lg px-3 py-2 text-sm
                                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[220px]"
          >
            <option value="">Select a game to tag…</option>
            <optgroup label="Assign to game">
              {weekGames.map((game) => (
                <option key={game.id} value={game.id}>
                  {game.team1?.name} vs {game.team2?.name} ·{" "}
                  {new Date(game.game_date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </option>
              ))}
            </optgroup>
            <optgroup label="Other">
              <option value="untag">Remove tag</option>
            </optgroup>
          </select>
          <button
            onClick={applyTag}
            disabled={tagging || selectedPhotoIds.size === 0 || !taggingGameId}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400
                                   text-white text-sm font-semibold rounded-lg transition"
          >
            {tagging ? "Saving…" : "Apply Tag"}
          </button>
          <button
            onClick={() => setSelectedPhotoIds(new Set())}
            className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400
                                   hover:text-gray-800 dark:hover:text-gray-200 transition"
          >
            Clear
          </button>
        </div>
      )}

      {/* ── Team filter (public, non-tag mode) ── */}
      {!tagMode && teams.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 shrink-0">
            Filter by team:
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterTeamId("all")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                filterTeamId === "all"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
            >
              All Photos
            </button>
            {teams.map((team) => (
              <button
                key={team.id}
                onClick={() => setFilterTeamId(String(team.id))}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                  filterTeamId === String(team.id)
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                }`}
              >
                {team.name}
              </button>
            ))}
            {filterTeamId !== "all" && (
              <span className="text-xs text-gray-400 dark:text-gray-500 self-center ml-1">
                {filteredPhotos.length} photo
                {filteredPhotos.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Photo grid */}
      {photosLoading ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          Loading photos…
        </div>
      ) : displayPhotos.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          {filterTeamId !== "all"
            ? "No tagged photos for this team yet."
            : "No photos in this album."}
        </div>
      ) : (
        <div
          className={`columns-2 sm:columns-3 md:columns-4 gap-2 space-y-2 ${tagMode ? "select-none" : ""}`}
        >
          {displayPhotos.map((photo, index) => {
            const isSelected = selectedPhotoIds.has(photo.id);
            const isTagged = !!photo.game_id;

            return (
              <button
                key={photo.id}
                onClick={(e) => handlePhotoClick(index, e)}
                className={`break-inside-avoid w-full overflow-hidden rounded-lg block
                                           relative transition-all duration-150 focus:outline-none
                                           ${
                                             tagMode
                                               ? isSelected
                                                 ? "ring-4 ring-orange-500 scale-[0.97]"
                                                 : "hover:opacity-80 cursor-pointer"
                                               : "hover:opacity-90 hover:scale-[1.01] focus:ring-2 focus:ring-orange-500"
                                           }`}
              >
                <LazyPhoto
                  photo={photo}
                  imgErrors={imgErrors}
                  setImgErrors={setImgErrors}
                />
                {photo.media_type === "video" && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-12 h-12 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center border border-white/30">
                      <span className="text-white text-lg ml-0.5">▶</span>
                    </div>
                  </div>
                )}

                {/* Tag mode overlay */}
                {tagMode && (
                  <div
                    className={`absolute inset-0 flex items-center justify-center transition ${
                      isSelected ? "bg-orange-500/30" : "bg-transparent"
                    }`}
                  >
                    {isSelected && (
                      <div className="w-7 h-7 rounded-full bg-orange-600 flex items-center justify-center shadow-lg">
                        <span className="text-white text-sm font-bold">✓</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Tagged game badge — shown in tag mode for context */}
                {tagMode && isTagged && !isSelected && (
                  <div className="absolute bottom-1.5 left-1.5 right-1.5">
                    <div className="bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded truncate">
                      {photo.game?.team1?.name} vs {photo.game?.team2?.name}
                    </div>
                  </div>
                )}
                {/* Save button */}
                {!tagMode && user && (
                  <button
                    onClick={(e) => toggleSave(photo.id, e)}
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition"
                  >
                    <Heart
                      size={14}
                      className={
                        savedPhotoIds.has(photo.id)
                          ? "fill-orange-500 text-orange-500"
                          : "text-white"
                      }
                    />
                  </button>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && displayPhotos[lightboxIndex] && (
        <div
          className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={lightboxPrev}
            className="absolute left-3 sm:left-6 text-white/70 hover:text-white text-3xl font-bold
                                   p-3 rounded-full hover:bg-white/10 transition z-10"
          >
            ‹
          </button>
          {displayPhotos[lightboxIndex].media_type === "video" ? (
            <iframe
              src={`https://drive.google.com/file/d/${displayPhotos[lightboxIndex].drive_file_id}/preview`}
              title={displayPhotos[lightboxIndex].filename}
              allow="autoplay; fullscreen"
              allowFullScreen
              className="w-[90vw] h-[70vh] max-w-4xl rounded-lg shadow-2xl border-0 bg-black"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={
                imgErrors[displayPhotos[lightboxIndex].id]
                  ? `https://drive.google.com/uc?export=view&id=${displayPhotos[lightboxIndex].drive_file_id}`
                  : displayPhotos[lightboxIndex].full_url
              }
              alt={displayPhotos[lightboxIndex].filename}
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              onError={() =>
                setImgErrors((prev) => ({
                  ...prev,
                  [displayPhotos[lightboxIndex].id]: true,
                }))
              }
            />
          )}
          <button
            onClick={lightboxNext}
            className="absolute right-3 sm:right-6 text-white/70 hover:text-white text-3xl font-bold
                                   p-3 rounded-full hover:bg-white/10 transition z-10"
          >
            ›
          </button>
          <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center gap-1 pointer-events-none">
            {displayPhotos[lightboxIndex].game && (
              <p className="text-white/60 text-xs">
                🏀 {displayPhotos[lightboxIndex].game.team1?.name} vs{" "}
                {displayPhotos[lightboxIndex].game.team2?.name}
              </p>
            )}
            <p className="text-white/40 text-xs font-mono">
              {displayPhotos[lightboxIndex].filename}
            </p>
            <p className="text-white/60 text-sm">
              {lightboxIndex + 1} / {displayPhotos.length}
            </p>
          </div>
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl p-2 rounded-full hover:bg-white/10 transition"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// ── Album Cover Strip ──────────────────────────────────────────────────────────
function AlbumCoverStrip({ albumId }) {
  const [covers, setCovers] = useState([]);

  useEffect(() => {
    supabase
      .from("photos")
      .select("drive_file_id, thumbnail_url")
      .eq("album_id", albumId)
      .limit(4)
      .then(({ data }) => setCovers(data || []));
  }, [albumId]);

  if (covers.length === 0) {
    return (
      <div className="w-full h-40 bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-4xl">
        📸
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-0.5 h-40 bg-gray-200 dark:bg-gray-700">
      {covers.slice(0, 4).map((photo, i) => (
        <img
          key={i}
          src={photo.thumbnail_url}
          alt=""
          className="w-full h-full object-cover"
        />
      ))}
      {Array.from({ length: Math.max(0, 4 - covers.length) }).map((_, i) => (
        <div key={`empty-${i}`} className="bg-gray-100 dark:bg-gray-700" />
      ))}
    </div>
  );
}
